import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Glass redesign — Phase 1 theme system.
 *
 * Three preferences:
 *   - `'system'` — follow the OS via `prefers-color-scheme`
 *   - `'light'`  — force light
 *   - `'dark'`   — force dark
 *
 * The actually-applied value (`resolvedTheme`) is always `'light'` or
 * `'dark'`; consumers that want to know "what's painted right now"
 * read that, not `theme`.
 *
 * Persistence: `localStorage` under `THEME_STORAGE_KEY`. We only
 * persist the user preference, never the resolved value — re-running
 * OS detection is cheap and means the user's machine theme follows
 * them across restarts.
 *
 * The provider applies the `.dark` class to `<html>` so all CSS
 * variables in `styles.css` (`.dark { ... }`) take effect. We also
 * set `color-scheme` so native form controls + scrollbars match.
 *
 * For Electron: the renderer process picks up `prefers-color-scheme`
 * automatically when the desktop wrapper sets `nativeTheme.themeSource`.
 * Future enhancement: bridge the user preference back to the main
 * process so the title bar / dock icon update too. v1 just relies on
 * matchMedia.
 *
 * StrictMode-safe: the listener is added in an effect with proper
 * cleanup. No setState during render.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'fluxter:theme';

interface ThemeContextValue {
  /** The user's stated preference. */
  theme: ThemePreference;
  /** The actually-applied theme after resolving 'system'. */
  resolvedTheme: ResolvedTheme;
  /** Update the preference and persist. */
  setTheme: (next: ThemePreference) => void;
  /** Cycle light → dark → system → light (used by the topbar toggle). */
  cycleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Private mode / disabled storage — silent fallback.
  }
  return 'system';
}

function detectSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  // Tells the UA what to draw native scrollbars / form controls in.
  root.style.colorScheme = resolved;
}

interface ThemeProviderProps {
  children: ReactNode;
  /**
   * Optional override for the default preference. Useful for tests
   * and Storybook. Real users always start from localStorage.
   */
  defaultTheme?: ThemePreference;
}

export const ThemeProvider = ({ children, defaultTheme }: ThemeProviderProps) => {
  const [theme, setThemeState] = useState<ThemePreference>(
    () => defaultTheme ?? readStoredPreference(),
  );
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => detectSystemTheme());

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  // Apply the resolved theme to the DOM whenever it changes.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Re-detect when the OS preference changes (only matters in 'system' mode,
  // but the listener is cheap and keeps `systemTheme` honest regardless).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent): void => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };
    // Older Safari uses addListener; modern browsers expose addEventListener.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler);
      return () => media.removeEventListener('change', handler);
    }
    media.addListener(handler);
    return () => media.removeListener(handler);
  }, []);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Private mode — preference reverts on reload.
    }
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState(current => {
      const next: ThemePreference =
        current === 'light' ? 'dark' : current === 'dark' ? 'system' : 'light';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // No-op.
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside a <ThemeProvider>');
  }
  return ctx;
};
